import type {
  BooleanOperator,
  DateOperator,
  PicklistOperator,
} from '@/common/zod-schemas/common/operators';
import type { ItemFilterOperator } from '@/common/zod-schemas/itemFilterSearchSchema';

/**
 * User-friendly URL terms for date filter operators.
 * These terms are used in URL parameters to make them more human-readable.
 */
export type UrlDateOperator = 'is' | 'after' | 'before' | 'in' | 'empty' | 'notempty';

/**
 * User-friendly URL terms for picklist filter operators.
 * These terms are used in URL parameters to make them more human-readable.
 */
export type UrlPicklistOperator = 'in' | 'empty' | 'notempty';

/**
 * User-friendly URL terms for boolean filter operators.
 * These terms are used in URL parameters to make them more human-readable.
 */
export type UrlBooleanOperator = 'is' | 'empty' | 'notempty';

/**
 * Maps user-friendly URL terms to internal date operators.
 * Single source of truth - reverse mapping is derived from this.
 *
 * Semantic distinction:
 * - 'is': Exact date match (internally creates midnight to midnight+1 range)
 * - 'after': Greater than date
 * - 'before': Less than date
 * - 'in': Continuous inclusive date range (start to end)
 * - 'empty': Has no value (null)
 * - 'notempty': Has a value (not null)
 */
export const urlToDateOperator = {
  is: 'eq',
  after: 'gt',
  before: 'lt',
  in: 'in',
  empty: 'null',
  notempty: 'notnull',
} as const satisfies Record<UrlDateOperator, DateOperator>;

/**
 * Derived mapping: Internal date operators to user-friendly URL terms.
 * Automatically generated from urlToDateOperator.
 */
export const dateOperatorToUrl = Object.fromEntries(
  Object.entries(urlToDateOperator).map(([url, internal]) => [internal, url]),
) as Record<DateOperator, UrlDateOperator>;

/**
 * Maps user-friendly URL terms to internal picklist operators.
 * Single source of truth - reverse mapping is derived from this.
 *
 * Semantic distinction:
 * - 'in': Set membership - "match any of {A, B, C}" (works for single or multiple values)
 * - 'empty': Has no value (null)
 * - 'notempty': Has a value (not null)
 */
export const urlToPicklistOperator = {
  in: 'in',
  empty: 'null',
  notempty: 'notnull',
} as const satisfies Record<UrlPicklistOperator, PicklistOperator>;

/**
 * Derived mapping: Internal picklist operators to user-friendly URL terms.
 * Automatically generated from urlToPicklistOperator.
 */
export const picklistOperatorToUrl = Object.fromEntries(
  Object.entries(urlToPicklistOperator).map(([url, internal]) => [internal, url]),
) as Record<PicklistOperator, UrlPicklistOperator>;

/**
 * Maps user-friendly URL terms to internal boolean operators.
 * Single source of truth - reverse mapping is derived from this.
 *
 * Semantic distinction:
 * - 'is': Exact boolean match (true or false)
 * - 'empty': Has no value (null)
 * - 'notempty': Has a value (not null)
 */
export const urlToBooleanOperator = {
  is: 'eq',
  empty: 'null',
  notempty: 'notnull',
} as const satisfies Record<UrlBooleanOperator, BooleanOperator>;

/**
 * Derived mapping: Internal boolean operators to user-friendly URL terms.
 * Automatically generated from urlToBooleanOperator.
 */
export const booleanOperatorToUrl = Object.fromEntries(
  Object.entries(urlToBooleanOperator).map(([url, internal]) => [internal, url]),
) as Record<BooleanOperator, UrlBooleanOperator>;

/**
 * Regex pattern for validating compact picklist filter format in URLs.
 * Dynamically generated from urlToPicklistOperator keys to ensure consistency.
 * Format: operator_value1-value2-...
 * Examples: in_50, in_30-34-35, empty, notempty
 */
export const PICKLIST_FILTER_REGEX = new RegExp(
  `^(${Object.keys(urlToPicklistOperator).join('|')})(_(\\d+(-\\d+)*))?$`,
);

export const SUBDIVISION_FILTER_SEPARATOR = ':';

export const toSubdivisionFilterValue = (countryCode: string, subdivisionCode: string): string =>
  `${countryCode}${SUBDIVISION_FILTER_SEPARATOR}${subdivisionCode}`;

export const subdivisionFilterValueCountry = (value: string): string =>
  value.split(SUBDIVISION_FILTER_SEPARATOR)[0] ?? '';

export const UNASSIGNED_SUBDIVISION_VALUE = '0';

export const selectableSubdivisions = (
  selectedCountryCodes: string[],
  selectedSubdivisions: string[],
): string[] =>
  selectedCountryCodes.length === 0
    ? []
    : selectedSubdivisions.filter(
        (value) =>
          value === UNASSIGNED_SUBDIVISION_VALUE ||
          selectedCountryCodes.includes(subdivisionFilterValueCountry(value)),
      );

const CODE_VALUE = `[A-Za-z0-9${SUBDIVISION_FILTER_SEPARATOR}-]+`;

export const CODE_VALUE_REGEX = new RegExp(`^${CODE_VALUE}$`);

export const CODE_FILTER_REGEX = new RegExp(`^in(_(${CODE_VALUE}(,${CODE_VALUE})*))?$`);

/**
 * Regex pattern for validating compact date filter format in URLs.
 * Dynamically generated from urlToDateOperator keys.
 * Format: operator_YYYY-MM-DD or operator_YYYY-MM-DD_YYYY-MM-DD
 * Examples: is_2025-11-12, after_2025-11-04, in_2024-01-01_2024-12-31, empty, notempty
 * Note: 'is' operator accepts single date in URL; end-of-day timestamp is computed by RSQL layer
 * Note: 'in' operator can be used without values when filter added but no dates selected yet
 */
export const DATE_FILTER_REGEX = new RegExp(
  `^(${Object.keys(urlToDateOperator).join('|')})(_(\\d{4}-\\d{2}-\\d{2}(_(\\d{4}-\\d{2}-\\d{2}))?)?)?$`,
);

/**
 * Regex pattern for validating compact boolean filter format in URLs.
 * Dynamically generated from urlToBooleanOperator keys to ensure consistency.
 * Format: operator_value
 * Examples: is_true, is_false, empty, notempty
 */
export const BOOLEAN_FILTER_REGEX = new RegExp(
  `^(${Object.keys(urlToBooleanOperator).join('|')})(_(true|false))?$`,
);

/**
 * Type guard to check if a string is a valid URL picklist operator.
 * Uses the urlToPicklistOperator mapping as the source of truth.
 */
export const isUrlPicklistOperator = (operator: string): operator is UrlPicklistOperator => {
  return Object.hasOwn(urlToPicklistOperator, operator);
};

/**
 * Type guard to check if a string is a valid URL date operator.
 * Uses the urlToDateOperator mapping as the source of truth.
 */
export const isUrlDateOperator = (operator: string): operator is UrlDateOperator => {
  return Object.hasOwn(urlToDateOperator, operator);
};

/**
 * Type guard to check if an ItemFilterOperator is a valid date operator.
 * Uses the dateOperatorToUrl mapping as the source of truth.
 */
export const isDateOperatorValid = (operator: ItemFilterOperator): operator is DateOperator => {
  return Object.hasOwn(dateOperatorToUrl, operator);
};

/**
 * Type guard to check if an ItemFilterOperator is a valid picklist operator.
 * Uses the picklistOperatorToUrl mapping as the source of truth.
 */
export const isPicklistOperatorValid = (
  operator: ItemFilterOperator,
): operator is PicklistOperator => {
  return Object.hasOwn(picklistOperatorToUrl, operator);
};

/**
 * Type guard to check if a string is a valid URL boolean operator.
 * Uses the urlToBooleanOperator mapping as the source of truth.
 */
export const isUrlBooleanOperator = (operator: string): operator is UrlBooleanOperator => {
  return Object.hasOwn(urlToBooleanOperator, operator);
};

/**
 * Type guard to check if an operator is a valid boolean operator.
 * Uses the booleanOperatorToUrl mapping as the source of truth.
 */
export const isBooleanOperatorValid = (operator: string): operator is BooleanOperator => {
  return Object.hasOwn(booleanOperatorToUrl, operator);
};

/**
 * Type guard to validate a regex match array for picklist filters.
 * Checks both the match structure and operator validity.
 */
export const isValidPicklistMatch = (
  match: RegExpMatchArray | null,
): match is [string, UrlPicklistOperator, string] => {
  return (
    match !== null &&
    match.length >= 3 &&
    match[1] !== undefined &&
    match[2] !== undefined &&
    isUrlPicklistOperator(match[1])
  );
};

/**
 * Type guard to validate a regex match array for date filters.
 * Checks both the match structure and operator validity.
 */
export const isValidDateMatch = (
  match: RegExpMatchArray | null,
): match is [string, UrlDateOperator, string] => {
  return (
    match !== null &&
    match.length >= 3 &&
    match[1] !== undefined &&
    match[2] !== undefined &&
    isUrlDateOperator(match[1])
  );
};

/**
 * Type guard to validate a regex match array for boolean filters.
 * Checks both the match structure and operator validity.
 */
export const isValidBooleanMatch = (
  match: RegExpMatchArray | null,
): match is [string, UrlBooleanOperator, string] => {
  return (
    match !== null && match.length >= 2 && match[1] !== undefined && isUrlBooleanOperator(match[1])
  );
};
